<template>
  <v-container class="px-5 my-5">
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg">
          <v-card-title
            class="text-h6 grey--text text--darken-3 font-weight-medium pt-8 mx-4"
            style="font-size: 20px !important;"
            >{{$t("goals.title")}}
            <v-spacer />
            <v-btn
              color="primary"
              class="mx-2 rounded-lg"
              @click="toggleDrawerGoal()"
            >
              <span style="color: #fff;">
                <v-icon>add</v-icon> {{$t("goals.create_button")}}
              </span>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-row>
            <v-col class="mx-4 mt-8" cols="12" sm="6" md="4">
              <v-text-field
                v-model="goalsTable.search"
                @keyup.enter="getGoalsData"
                @click:append="goalsTable.search ? getGoalsData : null"
                :label="$t('actions.search')"
                outlined
                clearable
                class="rounded-lg"
                prepend-inner-icon="search"
                dense
              />
            </v-col>
          </v-row>
          <template>
            <v-data-table
              :loading="goalsTable.loading"
              :headers="goalsTable.headers"
              :items="goalsTable.items"
              :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
              :items-per-page="goalsTable.pagination.itemsPerPage"
              :server-items-length="goalsTable.total"
              :no-data-text="$t('subscriptions_report.no_data')"
              :no-results-text="$t('subscriptions_report.no_data')"
              @update:options="goalsTable.pagination = $event"
            >
              <!-- SHOWS NO DATA -->
              <template v-slot:no-data>
                <div class="text-center pa-5 my-5">
                  <v-img
                    :src="require('@/assets/svg/box_empty.svg')"
                    height="150"
                    max-height="150px"
                    aspect-ratio="1"
                    contain
                  />
                  <div class="grey--text">
                    {{ $t("common.no_data") }}
                  </div>
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn small icon color="primary" @click="editItem(item)">
                  <v-icon small>
                    edit
                  </v-icon>
                </v-btn>
                <!-- <v-btn small icon color="red" @click="deleteItem(item)">
                  <v-icon small>
                    delete
                  </v-icon>
                </v-btn> -->
              </template>
            </v-data-table>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!------------------------------------------------->
    <!-- Drawer --------------------------------------->
    <!------------------------------------------------->

    <v-dialog v-model="drawer" persistent max-width="600px">
      <v-card style="border-radius: 16px; padding: 20px;">
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="toggleDrawerGoal()">
            {{$t("goals.close_button")}}
          </v-btn>
          <v-btn :disabled="disabled" color="primary" @click="save">
            {{$t("goals.save_button")}}
          </v-btn>
        </v-card-actions>
        <v-card-title class="d-flex align-items-center justify-center">
          <span class="text-h7">{{
            update ?  $t("goals.update")  : $t("goals.create") 
          }}</span>
        </v-card-title>
        <v-spacer></v-spacer>
        <v-divider></v-divider>
        <v-card-text class="mt-8">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              :label="$t('goals.name_label')"
              v-model="name"
              required
              v-validate="'required'"
              data-vv-name="name"
              outlined
              dense
              class="rounded-lg"
            ></v-text-field>
            <v-text-field
              :label="$t('goals.description_label')"
              v-model="description"
              required
              v-validate="'required'"
              data-vv-name="description"
              outlined
              dense
              class="rounded-lg"
            ></v-text-field>
            <v-autocomplete
              v-model="selectedSnippets"
              :items="snippets"
              item-value="id"
              item-text="name"
              label="snippets"
              class="rounded-lg"
              :rules="rules.snippetsRule"
              multiple
              clearable
              small-chips
              v-validate="'required'"
              data-vv-name="selectedSnippets"
              outlined
              dense
            >
              <template v-slot:prepend-item>
                <v-list-item ripple>
                  <v-list-item-action>
                    <v-checkbox
                      v-model="allSnippets"
                      @change="toggleSelectAllSnippets()"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="selectedOrganizations"
              :items="organizations"
              item-value="id"
              item-text="name"
              :label="
                $t(
                  'notifications.form.inputs.send_methods.specific.organizations'
                )
              "
              class="rounded-lg"
              :rules="rules.organizationsRule"
              multiple
              clearable
              small-chips
              required
              v-validate="'required'"
              data-vv-name="selectedOrganizations"
              outlined
              dense
            >
              <template v-slot:prepend-item>
                <v-list-item ripple>
                  <v-list-item-action>
                    <v-checkbox
                      v-model="all"
                      @change="toggleSelectAll()"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-autocomplete>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import organizationMixin from "@/mixins/organization";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import BaseToolbar from "@/components/BaseToolbar.vue";
import { organizationService } from "@/services/organization.service";

export default {
  name: "Goals",
  mixins: [organizationMixin],
  components: {
    BaseToolbar
  },
  data() {
    return {
      valid: false,
      disabled: true,
      goalsTable: {
        loading: false,
        items: [],
        search: null,
        total: 0,
        pagination: {
          sortBy: ["created_at"],
          sortDesc: [true],
          itemsPerPage: 10
        },
        headers: [
          {
            text: "ID",
            align: "center",
            sortable: true,
            value: "id",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("common.name"),
            align: "center",
            sortable: true,
            value: "name",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("common.description"),
            align: "center",
            sortable: true,
            value: "description",
            class: "text-no-wrap blue--text text--darken-4"
          },
          {
            text: this.$t("dates.date"),
            align: "center",
            sortable: true,
            value: "created_at",
            class: "text-no-wrap blue--text text--darken-4"
          },
          { text: "", value: "actions", sortable: false }
        ]
      },
      drawer: false,
      organizations: [],
      name: "",
      description: "",
      selectedOrganizations: [],
      selectedSnippets: [],
      all: false,
      allSnippets: false,
      update: false,
      goalToUpdate: null,
      rules: {
        nameRules: [
          v => !!v || "Name is required",
          v => (v && v.length <= 10) || "Name must be less than 10 characters"
        ],
        descriptionRules: [
          v => !!v || "Description is required",
          v => (v && v.length >= 10) || "Name must be less than 10 characters"
        ],
        organizationsRule: [
          v => !!v || this.$t("notifications.form.rules.organizations.required")
        ],
        snippetsRule: [
          v => !!v || this.$t("notifications.form.rules.organizations.required")
        ]
      }
    };
  },
  async created() {
    await this.getAllOrganizations();
    await this.getSnippets();
    await this.getGoalsData();
  },
  watch: {
    "goalsTable.pagination": {
      async handler() {
        await this.getGoalsData();
      },
      deep: true
    },
    selectedOrganizations: {
      async handler() {
        const isValid = await this.$validator.validateAll();
        this.disabled = !isValid;
      }
    },
    name: {
      async handler() {
        const isValid = await this.$validator.validateAll();
        this.disabled = !isValid;
      }
    },
    description: {
      async handler() {
        const isValid = await this.$validator.validateAll();
        this.disabled = !isValid;
      }
    },
    selectedSnippets: {
      async handler() {
        const isValid = await this.$validator.validateAll();
        this.disabled = !isValid;
      }
    },
    goals: {
      async handler() {
        this.goalsTable.items = this.goals;
        this.goalsTable.total = this.totalGoals;
      }
    }
  },
  computed: {
    ...mapGetters({
      goals: "goals/goals",
      snippets: "goals/snippets",
      goal: "goals/goal",
      totalGoals: "goals/totalGoals"
    })
  },
  methods: {
    ...mapActions({
      getGoals: "goals/getGoals",
      getSnippets: "goals/getSnippets",
      createGoal: "goals/createGoal",
      updateGoal: "goals/updateGoal",
      getGoal: "goals/getGoal"
    }),
    async getAllOrganizations() {
      try {
        const {
          data: organizations
        } = await organizationService.getAllOrganizations();

        this.organizations = organizations;
      } catch (error) {
        console.log(error);
      }
    },

    async getGoalsData() {
      this.goalsTable.loading = true;

      let { sortBy, sortDesc = false } = this.goalsTable.pagination;
      const { page, itemsPerPage } = this.goalsTable.pagination;

      if (Array.isArray(sortBy)) {
        [sortBy] = sortBy;
      }
      if (Array.isArray(sortDesc)) {
        [sortDesc] = sortDesc;
      }
      const res = await this.getGoals({
        search: this.goalsTable.search,
        page,
        rowsPerPage: itemsPerPage,
        sortBy: sortBy || "id",
        descending: sortDesc,
        search: this.goalsTable.search
      });

      this.goalsTable.items = this.goals;
      this.goalsTable.total = this.totalGoals;

      this.goalsTable.loading = false;
    },
    async editItem(goal) {
      this.update = true;
      this.goalToUpdate = goal.id;
      await this.getGoal(goal.id);

      const goalToUpdate = JSON.parse(JSON.stringify(this.goal));

      this.name = goalToUpdate.name;
      this.description = goalToUpdate.description;
      this.selectedOrganizations = goalToUpdate.organizations;
      this.selectedSnippets = goalToUpdate.snippets;
      this.drawer = true;
    },
    deleteItem() {},
    toggleDrawerGoal() {
      this.drawer = !this.drawer;

      if (!this.drawer) {
        this.name = "";
        this.description = "";
        this.selectedOrganizations = [];
        this.selectedSnippets = [];
        this.update = false;
      }
    },
    async save() {
      if (this.update) {
        await this.updateGoal({
          id: this.goalToUpdate,
          name: this.name,
          description: this.description,
          organizations: this.selectedOrganizations,
          snippets: this.selectedSnippets
        });
        await this.getGoalsData()
        this.toggleDrawerGoal();
        return;
      }
      await this.createGoal({
        name: this.name,
        description: this.description,
        organizations: this.selectedOrganizations,
        snippets: this.selectedSnippets
      });
      await this.getGoalsData()
      this.toggleDrawerGoal();
    },
    toggleSelectAll() {
      if (!this.all) {
        this.selectedOrganizations = [];
        return;
      }
      this.selectedOrganizations = [];
      this.organizations.forEach(org =>
        this.selectedOrganizations.push(org.id)
      );
    },
    toggleSelectAllSnippets() {
      if (!this.allSnippets) {
        this.selectedSnippets = [];
        return;
      }
      this.selectedSnippets = [];
      this.snippets.forEach(org => this.selectedSnippets.push(org.id));
    }
  }
};
</script>
